import { userApi } from '@/api/account-management/userApi';
import { createUser, type User } from '@/models/account-management/User';
import AddIcon from '@mui/icons-material/Add';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { fillHeight } from "../../theme";
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Dialog from '../common/dialog/Dialog';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import LocationTransferList from '../common/transfer-list/LocationTransferList';
import RoleTransferList from './RoleTransferList';
import { useCurrentUser } from '../../providers/currentUser/CurrentUserProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { isValidEmail } from '../../validation/fieldValidators';
import { errorMessage } from '@/api/common';
import { Perm } from '../../permissions/permissions';
import TabWithError from  '../common/TabWithError';

const UserList = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitleName, setDialogTitleName] = useState<string | undefined>();
    const [tabValue, setTabValue] = useState<string>('detail');
    const { control, reset, register, handleSubmit: onFormSubmit, formState } = useForm<User>();
    const qc = useQueryClient();
    const app = useAppContext();
    // Users follow the viewed org (super-admins can manage any org); the query re-runs when the org changes.
    const { data: userList, isPending: isGridLoading } = useQuery<User[]>({ queryKey: ['users', app.organizationId], queryFn: () => userApi.list(app.organizationId), staleTime: Infinity });
    const { mutateAsync: saveUser, isPending: isSaving } = useMutation({ mutationFn: (u: User) => userApi.save(u, app.organizationId), onSuccess: () => handleSaveSuccess(), onError: (e) => handleSaveError(e) });
    const { mutateAsync: deleteUser } = useMutation({ mutationFn: userApi.delete, onSuccess: () => handleDeleteSuccess(), onError: () => handleDeleteError() }); //react-query to delete the user
    const toast = useToast();
    const strings = useLocalizedStrings();
    const currentUser = useCurrentUser();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.users);
    const canUpdate = perms.canUpdate(Perm.users);
    const canDelete = perms.canDelete(Perm.users);
    
    // Creating and editing are different permissions. Gating both on canUpdate hid Save from someone with
    // add-but-not-edit rights, so they could open the new-user form and had no way to submit it.
    // dialogTitleName is only set when editing an existing user.
    const dialogReadOnly = dialogTitleName ? !canUpdate : !canCreate;

    const fullName = (user: User | undefined) => user ? `${user.firstName} ${user.lastName}` : '';

    const columns: GridColDef<User>[] = [
        {
            field: 'name',
            headerName: strings.name,
            valueGetter: (_value, row) => `${row.firstName} ${row.lastName}`,
            flex: 2,
        },
        {
            field: 'email',
            headerName: strings.email,
            flex: 2,
        },
        {
            field: 'roles',
            headerName: strings.roles,
            valueGetter: (_value, row) => row.roles.map(r => r.name).join(', '),
            flex: 3
        },
        {
            field: 'status',
            headerName: strings.status,
            flex: 1
        },
        {
            field: 'enabled',
            headerName: strings.enabled,
            type: 'boolean',
            flex: 1
        },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [
                <DataGridEditButton id={params.id.toString()} onClick={handleEditClick} aria-label="edit user" readOnly={!canUpdate} />,
                <DataGridDeleteButton id={params.id.toString()} getWarningMessage={getDeleteWarningMessage} onClick={handleDeleteClick} aria-label="delete user" hidden={!canDelete || params.id === currentUser?.user?.id} />
            ],
        },
    ];

    useEffect(() => {
        if (!dialogOpen) {
            setDialogTitleName(undefined);
            reset(undefined);
            setTabValue('detail');
        }
    }, [dialogOpen, reset]);

    const handleAddClick = () => {
        reset(createUser());
        setDialogOpen(true);
    };

    const handleEditClick = (id: string) => {
        const user = userList?.find(u => u.id === id);
        if (!user) return;
        reset(user);
        setDialogTitleName(fullName(user));
        setDialogOpen(true);
    };

    const getDeleteWarningMessage = (id: string) => {
        const user = userList?.find(o => o.id === id);
        return strings.userConfirmDeleteMessgage.replace("{{name}}", fullName(user));
    }

    const handleDeleteClick = async (id: string) => {
        await deleteUser(id);
    };

    const handleDeleteSuccess = async () => {
        toast({ severity: "success", message: strings.userDeleteSuccess });
        await qc.invalidateQueries({ queryKey: ['users'] });
        // Role > Users lists the members of each role, so it goes stale too.
        await qc.invalidateQueries({ queryKey: ['roles'] });
    }

    const handleDeleteError = () => {
        toast({ severity: "error", message: strings.userDeleteError });
    }

    const handleSubmit: SubmitHandler<User> = async (data) => {
        console.log(data);
        await saveUser(data); // Run the save mutation with the current form data
    }

    const handleSaveSuccess = async () => {
        toast({ severity: "success", message: strings.userSaveSuccess });
        await qc.invalidateQueries({ queryKey: ['users'] });
        // A user's role assignment changed here, so Role > Users must refetch.
        await qc.invalidateQueries({ queryKey: ['roles'] });
        setDialogOpen(false);
    }

    // Some save failures have a specific reason the server can state better than we can — disabling your own
    // account, for one — so prefer its explanation and fall back to the generic wording when it sends none.
    const handleSaveError = (e: unknown) => {
        toast({ severity: "error", message: errorMessage(e, strings.userSaveError) });
    }
   
    const tabErrors = {
        detail: !!(
            formState.errors.firstName ||
            formState.errors.lastName
        )
    };

    return (
        <>
            <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
                {canCreate &&
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>{strings.userNewTitle}</Button>
                        </Box>
                        <Spacer y={2} />
                    </>
                }
                <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                    <DataGrid
                        rows={userList}
                        columns={columns}
                        // columnVisibilityModel={{
                        //     role: !app.isSuperAdmin(), // Hide the roles column for super admins                             
                        // }}
                        autoPageSize={ true}
                        loading={ isGridLoading }
                        localeText={{ noRowsLabel: strings.usersNotFound }}
                    />
                </Box>
            </Box>

            <form onSubmit={onFormSubmit(handleSubmit)} id="dialog-user-form">
                {/* TabContext wraps the Dialog rather than sitting inside it, because the tab bar is passed
                    through the `tabs` slot and has to resolve the same context as the panels below. */}
                <TabContext value={tabValue}>
                <Dialog
                    open={dialogOpen}
                    title={dialogTitleName ? strings.userEditTitle.replace("{{name}}", dialogTitleName) : strings.userNewTitle}
                    formId="dialog-user-form"
                    formIsDirty={formState.isDirty}
                    saving={isSaving}
                    saveButtonTitle={strings.save}
                    onCancel={() => setDialogOpen(false)}
                    cancelButtonTitle={strings.cancel}
                    pad={false}
                    readOnly={dialogReadOnly}
                    tabs={
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: (theme) => theme.palette.background.paper }}>
                            <TabList onChange={(_, newValue) => setTabValue(newValue)} aria-label="tabs">
                                <TabWithError label={strings.details} value="detail" hasError={tabErrors.detail} />
                                <Tab label={strings.roles} value="roles" />
                                <Tab label={strings.locations} value="locations" />
                            </TabList>
                        </Box>
                    }
                >
                        {/* <Box sx={!app.isSuperAdmin() ? { width: 736, height: 365, overflow: 'hidden' } : {}}> Set fixed size when tabs are shown to prevent dialog resizing */}
                        <Box sx={{ width: 736, height: 416, overflow: 'hidden' }}>
                            <TabPanel id="detail" value="detail" keepMounted>
                                <TextField
                                    id="firstName"
                                    label={strings.firstName}
                                    variant="outlined"
                                    sx={{ width: 400 }}
                                    {...register("firstName", {
                                        required: strings.validationFirstNameRequired
                                    })}
                                    error={!!formState.errors.firstName}
                                    helperText={formState.errors.firstName?.message || " "}
                                />
                                <Spacer />
                                <TextField
                                    id="lastName"
                                    label={strings.lastName}
                                    variant="outlined"
                                    sx={{ width: 400 }}
                                    {...register("lastName", {
                                        required: strings.validationLastNameRequired
                                    })}
                                    error={!!formState.errors.lastName}
                                    helperText={formState.errors.lastName?.message || " "}
                                />
                                <Spacer />
                                <TextField
                                    id="email"
                                    label={strings.email}
                                    variant="outlined"
                                    disabled={!!dialogTitleName}
                                    sx={{ width: 400 }}
                                    {...register("email", {
                                        required: strings.validationEmailRequired,
                                        // Format is only worth checking once something has been entered;
                                        // `required` already covers the empty case.
                                        validate: (v?: string) => !v?.trim() || isValidEmail(v) || strings.validationEmailInvalid,
                                    })}
                                    error={Boolean(formState.errors.email)}
                                    helperText={formState.errors.email?.message || " "}
                                />
                                <Spacer />
                                <FormControl size="input">
                                    <FormControlLabel
                                        label={strings.enabled}
                                        control={
                                            <Controller
                                                name="enabled"
                                                control={control}
                                                render={({ field }) => (
                                                    <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                                )}
                                            />
                                        }
                                    />
                                </FormControl>
                            </TabPanel>
                            <TabPanel id="roles" value="roles" keepMounted>
                                <Controller
                                    name="roles"
                                    control={control}
                                    render={({ field }) => (
                                        <RoleTransferList
                                            selected={field.value}
                                            setSelected={field.onChange}
                                            listWidth={300}
                                            listHeight={230}
                                        />
                                    )}
                                />
                            </TabPanel>
                            <TabPanel id="locations" value="locations" keepMounted>
                                <Controller
                                    name="locationIds"
                                    control={control}
                                    render={({ field }) => (
                                        <LocationTransferList
                                            selected={field.value}
                                            setSelected={field.onChange}
                                            listWidth={300}
                                            listHeight={230}
                                        />
                                    )}
                                />
                            </TabPanel>
                        </Box>
                </Dialog>
                </TabContext>
            </form>
        </>
    );
};

export default UserList;